import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Structured Data (JSON-LD) Components for SEO
 * These help search engines understand the content better
 */

// Organization Schema - Company information
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ASAP Capital",
    "alternateName": "ASAP Capital Holdings",
    "url": "https://asapcapitalholdings.com",
    "logo": "https://customer-assets.emergentagent.com/job_realestate-fund-7/artifacts/hywwykpi_eb79b399-a596-460e-85f0-525cccff1722.png",
    "description": "A Reg D 506(c) private equity real estate fund specializing in value-add multifamily and commercial investments in Greater Houston, Texas.",
    "foundingDate": "2005",
    "founders": [{
      "@type": "Person",
      "name": "ASAP Capital Leadership Team"
    }],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Houston",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Investor Relations",
      "email": "info@asapcapital.net",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://www.instagram.com/yogesh.patil.official/",
      "https://www.facebook.com/profile.php?id=61584155613629",
      "https://www.youtube.com/@yogeshpatil8339",
      "https://www.tiktok.com/@yogeshpat8",
      "https://www.linkedin.com/in/yogesh-patil-95468a78/"
    ],
    "areaServed": {
      "@type": "Place",
      "name": "Greater Houston, Texas"
    },
    "knowsAbout": [
      "Real Estate Investment",
      "Private Equity",
      "Multifamily Housing",
      "Commercial Real Estate",
      "Value-Add Investing"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Investment Fund Schema
export const InvestmentFundSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "InvestmentFund",
    "name": "ASAP Capital Value-Add Real Estate Fund",
    "description": "A Reg D 506(c) private equity fund targeting 20-28% IRR through value-add real estate investments in Greater Houston.",
    "provider": {
      "@type": "Organization",
      "name": "ASAP Capital"
    },
    "investmentType": "Private Equity Real Estate",
    "category": "Value-Add Real Estate",
    "areaServed": {
      "@type": "Place",
      "name": "Greater Houston, Texas"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Local Business Schema for Houston presence
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "ASAP Capital",
    "description": "Houston-based private equity real estate investment firm specializing in value-add multifamily and commercial properties.",
    "url": "https://asapcapitalholdings.com",
    "telephone": "+1-346-310-0995",
    "email": "info@asapcapital.net",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Houston",
      "addressRegion": "TX",
      "postalCode": "77002",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "29.7604",
      "longitude": "-95.3698"
    },
    "openingHours": "Mo-Fr 09:00-17:00",
    "priceRange": "$$$",
    "serviceType": [
      "Private Equity Investment",
      "Real Estate Investment",
      "Investment Management"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema - for FAQ page
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Article Schema - for insights/blog posts
export const ArticleSchema = ({ article }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.publishDate,
    "dateModified": article.updatedDate || article.publishDate,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "ASAP Capital",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_realestate-fund-7/artifacts/hywwykpi_eb79b399-a596-460e-85f0-525cccff1722.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://asapcapitalholdings.com/insights/${article.slug}`
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Website Schema with Search Action
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ASAP Capital",
    "url": "https://asapcapitalholdings.com",
    "description": "Private equity real estate investments for accredited investors. Value-add multifamily and commercial properties in Greater Houston.",
    "publisher": {
      "@type": "Organization",
      "name": "ASAP Capital"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Real Estate Investment Schema
export const RealEstateInvestmentSchema = ({ property }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "ASAP Capital",
    "description": `Investment opportunity: ${property.name} - ${property.type} property in ${property.location}`,
    "areaServed": {
      "@type": "Place",
      "name": property.location
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Product",
        "name": property.name,
        "description": `${property.type} investment property - ${property.units}`,
        "category": property.type
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema for Investment Services
export const InvestmentServiceSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Private Equity Real Estate Investment",
    "name": "ASAP Capital Investment Services",
    "description": "Value-add real estate investment opportunities for accredited investors, family offices, and institutional investors.",
    "provider": {
      "@type": "Organization",
      "name": "ASAP Capital"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Greater Houston, Texas"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Accredited Investors"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Investment Products",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Direct Fund Investment",
            "description": "Minimum $100,000 investment in ASAP Capital Value-Add Fund"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Co-Investment Opportunities",
            "description": "Direct property co-investment for accredited investors"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Case Study Schema for individual case study pages
export const CaseStudySchema = ({ caseStudy }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${caseStudy.name} - Real Estate Investment Case Study`,
    "description": caseStudy.description || `Value-add real estate investment case study: ${caseStudy.name} in ${caseStudy.location}`,
    "image": caseStudy.image,
    "author": {
      "@type": "Organization",
      "name": "ASAP Capital"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ASAP Capital",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_realestate-fund-7/artifacts/hywwykpi_eb79b399-a596-460e-85f0-525cccff1722.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://asapcapital.net/case-studies/${caseStudy.slug}`
    },
    "about": {
      "@type": "RealEstateAgent",
      "name": "ASAP Capital",
      "areaServed": {
        "@type": "Place",
        "name": caseStudy.location
      }
    },
    "mentions": {
      "@type": "Place",
      "name": caseStudy.location,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": caseStudy.location?.split(',')[0]?.trim() || "Houston",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    }
  };

  // Add performance metrics if available
  if (caseStudy.irr && caseStudy.irr !== '-') {
    schema.keywords = `real estate investment, ${caseStudy.type}, ${caseStudy.irr} IRR, Houston real estate, value-add investment`;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default {
  OrganizationSchema,
  InvestmentFundSchema,
  LocalBusinessSchema,
  FAQSchema,
  ArticleSchema,
  BreadcrumbSchema,
  WebsiteSchema,
  RealEstateInvestmentSchema,
  InvestmentServiceSchema,
  CaseStudySchema
};
